import React, { Component, Suspense } from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import storeLoader from 'app/stores/storeLoader';
import { Box, Button, Flex, MenuItem, Spinner, Text } from 'core/components';
import { Field, FormComponent, Select } from 'core/form';
import DataViewWrapper from 'app/components/dataviews/DataViewWrapper';
import DataViewModel from 'app/stores/query/DataViewModel';
import { getObjectForHash } from 'app/stores/query/urlHash';
import $savedViews from 'app/stores/savedView/$savedViews';
import MetricsExplorerResult from 'app/views/metrics/MetricsExplorerResult';
import WidgetFrame from '../WidgetFrame';
import withConfigOptions from '../withConfigOptions';

function SelectWithFilter(props) {
  return <Select {...props} fill showFilter />;
}

@inject('$metrics')
@storeLoader('$savedViews.collection')
@withTheme
@withConfigOptions
@observer
class SavedViewWidget extends Component {
  static fields = {
    savedViewId: {
      label: 'Saved View',
      rules: 'required',
      options: [],
      component: SelectWithFilter,
      getFieldProps() {
        const options = $savedViews.collection.get().map((view) => ({ value: view.id, label: view.name }));
        return { options };
      }
    }
  };

  static getDefaultTitle(config) {
    const savedView = config.savedViewId ? $savedViews.collection.get(config.savedViewId) : null;
    return savedView ? savedView.name : 'Saved View';
  }

  state = {
    loading: true,

    // for Flow
    dataview: new DataViewModel(),

    // store the model for the saved view
    savedView: undefined,

    // for NMS
    query: undefined
  };

  componentDidMount() {
    const { config } = this.props;
    const { savedViewId } = config;
    this.fetchAndRenderView(savedViewId);
  }

  componentDidUpdate(prevProps) {
    const { config } = this.props;
    const { savedViewId } = config;

    if (savedViewId && savedViewId !== prevProps.config.savedViewId) {
      this.fetchAndRenderView(savedViewId);
    }
  }

  fetchAndRenderView(viewId) {
    const { dataview } = this.state;
    this.setState({ loading: true, savedView: undefined });

    $savedViews.collection.fetch().then(() => {
      const view = $savedViews.collection.get(viewId);
      this.setState({ savedView: view, loading: false });

      if (view) {
        const hash = view.get('saved_query_id');

        if (view.isNMS) {
          getObjectForHash(hash).then((response) => {
            const query = response;
            this.setState({ query });
          });
        } else if (view) {
          dataview.initializeHash(hash);
          this.setState({ dataview });
        }
      }
    });
  }

  renderConfigurePanel() {
    const { handleCancelConfiguration } = this.props;
    const { model } = this.props;
    const { fields } = SavedViewWidget;

    return (
      <FormComponent fields={fields} model={model} options={{ name: 'Configure Saved Views Widget' }}>
        {({ form }) => (
          <Flex flexDirection="column" flex={1} p={2}>
            <Box flex={1}>
              <Field name="savedViewId" large fill {...fields.savedViewId.getFieldProps()}>
                <Select fill showFilter />
              </Field>
            </Box>
            <Flex>
              <Button text="Cancel" onClick={() => handleCancelConfiguration(form)} mr={1} minWidth={100} />
              <Button
                text="Save"
                intent="primary"
                disabled={!form.valid}
                onClick={() => this.handleSave(form)}
                minWidth={100}
              />
            </Flex>
          </Flex>
        )}
      </FormComponent>
    );
  }

  renderWidgetContent() {
    const { theme } = this.props;
    const { dataview, loading, savedView, query } = this.state;

    if (loading) {
      return null;
    }

    if (savedView?.isNMS) {
      if (!query) {
        return <Text>Something went wrong!</Text>;
      }

      return (
        <Flex flexDirection="column" flex={1}>
          <MetricsExplorerResult query={query} isWidget />
        </Flex>
      );
    }

    // we want the chart to update on theme change however attempting to use allowChartUpdate results in a half-baked chart
    // instead we're opting to go nuclear and change the key of the component on theme change
    return (
      <DataViewWrapper
        key={`${dataview.hash}-${theme.name}`}
        dataview={dataview}
        viewProps={{
          showTooltips: true,
          height: '100%',
          showNativeLegend: true,
          flex: 1,
          chartConfig: {
            tooltip: {
              outside: true,
              useHTML: true
            }
          }
        }}
        allowCache
      />
    );
  }

  handleSave(form) {
    const { handleSaveConfiguration } = this.props;
    const newSavedViewId = form.getValue('savedViewId');

    handleSaveConfiguration(form);
    this.fetchAndRenderView(newSavedViewId);
  }

  renderMenuOptions = () => {
    const { savedView } = this.state;

    if (!savedView) {
      return null;
    }

    return (
      <MenuItem
        icon="series-search"
        text={savedView.isNMS ? 'Edit in Metrics Explorer' : 'Edit in Data Explorer'}
        to={savedView.navigatePath}
      />
    );
  };

  render() {
    const {
      canCustomize,
      onRemove,
      config,
      isConfigurePanelOpen: isConfigurePanelOpenProp,
      handleShowConfigurePanel,
      title,
      ...props
    } = this.props;
    const { loading } = this.state;
    const savedView = config.savedViewId ? $savedViews.collection.get(config.savedViewId) : null;
    const isConfigurePanelOpen = isConfigurePanelOpenProp || (!loading && !savedView);
    const fallbackTitle = isConfigurePanelOpen || !config.savedViewId ? 'Select a Saved View' : 'Loading...';

    return (
      <WidgetFrame
        canCustomize={canCustomize}
        menuOptions={!loading && this.renderMenuOptions()}
        configAction={handleShowConfigurePanel}
        onRemove={onRemove}
        title={title || SavedViewWidget.getDefaultTitle(config) || fallbackTitle}
        display="flex"
        {...props}
      >
        <Suspense loading={loading} fallback={<Spinner size={20} />}>
          {isConfigurePanelOpen && this.renderConfigurePanel()}
          {!isConfigurePanelOpen && this.renderWidgetContent()}
        </Suspense>
      </WidgetFrame>
    );
  }
}

export default SavedViewWidget;
